import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableRow,
  Tooltip,
  TableHead,
  IconButton,
  Button,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Typography,
  Chip,
  alpha,
  Divider,
  CircularProgress,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  GetNextGoodsOutNo,
  createIssue,
  ApproveSolistapi,
  getDivisionUnits,
  updateIssue,
  getCategories,
  fetchItemsByCategoryAndSubcategory,
  getSizes,
} from "../../../Services/Services.jsx";
import { useNavigate, useLocation } from "react-router-dom";
import { debounce } from "lodash";

// ── Helpers ──
const createEmptyRow = (isExtra = true) => ({
  size: null,
  category: null,
  item_description: null,
  description: "",
  uom: "",
  mos_req_qty: 0,
  already_issued: 0,
  pending_qty: 0,
  stock_qty: "0",
  issue_qty: "0",
  price: "0",
  amount: "0.00",
  is_extra: isExtra,
});

const getSelectedDivision = () => {
  try {
    const stored = localStorage.getItem("selectedDivision");
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const mergeSelectedIntoOptions = (options, selectedValue) => {
  const opts = Array.isArray(options) ? options : [];
  if (selectedValue && !opts.some((opt) => opt?.id === selectedValue?.id)) {
    return [selectedValue, ...opts];
  }
  return opts;
};

export default function IssueForm() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const disabledBg = isDark ? alpha(theme.palette.common.white, 0.06) : "#f5f5f5";
  const tableHeadBg = isDark ? alpha(theme.palette.primary.main, 0.15) : "#f0f4ff";
  const rowBg = (index) =>
    index % 2 === 0
      ? isDark ? alpha(theme.palette.common.white, 0.03) : "#fafafa"
      : theme.palette.background.paper;

  const storedDivision = getSelectedDivision();
  const navigate = useNavigate();
  const location = useLocation();
  const issueData = location.state?.issue || null;
  const isEditMode = !!issueData?.issue_id;

  // ── States ──
  const [unitOptions, setUnitOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [itemOptions, setItemOptions] = useState({});
  const [rowCategoryOptions, setRowCategoryOptions] = useState({}); 
  const [sizeOptions, setSizeOptions] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [rowErrors, setRowErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const [hasDraft, setHasDraft] = useState(false);

  const [formData, setFormData] = useState({
    go_no: "",
    slip_no: "",
    labour_name: "",
    division: storedDivision?.division_id || "",
    division_name: storedDivision?.division || "",
    remarks: "",
    project: "",
    created_by: localStorage.getItem("user_id"),
  });

  const [rows, setRows] = useState([createEmptyRow()]);

  // ── Initial fetch ──
  useEffect(() => {
    const init = async () => {
      try {
        const [divUnits, sizeData] = await Promise.all([
          getDivisionUnits(),
          getSizes(),
        ]);

        const units = divUnits.units || [];
        const sizesData = Array.isArray(sizeData) ? sizeData : sizeData?.results || [];

        setUnitOptions(units);
        setSizeOptions(sizesData);

        fetchProjects(1, {});

        if (isEditMode && issueData) {
          loadEditData(units, sizesData);
        } else {
          const draftForm = sessionStorage.getItem("issueDraft");
          const draftRows = sessionStorage.getItem("issueRows");
          if (draftForm && draftRows) {
            try {
              setFormData(JSON.parse(draftForm));
              setRows(JSON.parse(draftRows));
              setHasDraft(true);
              toast.info("Loaded Issue draft");
            } catch (e) {
              fetchNextGoodsOutNo();
            }
          } else {
            fetchNextGoodsOutNo();
          }
        }
      } catch (err) {
        toast.error("Failed to load initial dropdowns");
      }
    };
    init();
  }, [isEditMode]);

  // ── Load Edit Data ──
  const loadEditData = (units, sizesData) => {
    const divisionData = getSelectedDivision();

    setFormData({
      go_no: issueData.go_no || "",
      slip_no: issueData.slip_no || "",
      labour_name: issueData.labour_name || "",
      division: divisionData?.division_id || issueData.division || "",
      division_name: divisionData?.division || issueData.division_name || "",
      remarks: issueData.remarks || "",
      project: issueData.project || "",
      created_by: issueData.created_by || localStorage.getItem("user_id"),
    });

    const mappedItems = (issueData.items || []).map((item) => {
      const resolvedUom = item.uom_name || item.uom || "";
      const initialRate = item.price !== undefined ? String(item.price) : "0";
      const initialAmount = item.amount !== undefined ? String(item.amount) : "0.00";
      const stockQty = parseFloat(item.stock_qty || 0);

      return {
        size: item.size ? { id: item.size, size: item.size_name || "" } : null,
        category: item.category ? { id: item.category, name: item.category_name || "" } : null,
        item_description: {
          id: item.item,
          name: item.item_name || item.item_description || "",
          unit_name: resolvedUom,
          current_stock: stockQty,
          price: item.price || 0,
        },
        uom: resolvedUom,
        description: item.item_description || "",
        mos_req_qty: parseFloat(item.stock_qty || 0),
        already_issued: parseFloat(item.issue_qty || 0),
        pending_qty: Math.max(0, parseFloat(item.stock_qty || 0) - parseFloat(item.issue_qty || 0)),
        stock_qty: String(stockQty),
        issue_qty: String(item.issue_qty || "0"),
        price: initialRate,
        amount: initialAmount,
        is_extra: false,
      };
    });

    setRows(mappedItems.length ? mappedItems : [createEmptyRow()]);
  };

  const fetchNextGoodsOutNo = async () => {
    try {
      const data = await GetNextGoodsOutNo();
      if (data?.go_no) {
        setFormData((prev) => ({ ...prev, go_no: data.go_no }));
      }
    } catch {
      toast.error("Failed to fetch Goods Out No");
    }
  };

  const fetchProjects = async (page = 1, filters = {}) => {
    try {
      setProjectLoading(true);
      const divisionData = getSelectedDivision();
      const divisionId = divisionData?.division_id || localStorage.getItem("division_id") || "";
      const finalFilters = divisionId
        ? { ...filters, division: divisionId }
        : filters;
      const response = await ApproveSolistapi(page, finalFilters);
      const results = Array.isArray(response)
        ? response
        : Array.isArray(response?.results)
        ? response.results
        : [];
      if (page === 1) setProjectOptions(results);
      else setProjectOptions((prev) => [...prev, ...results]);
    } catch {
      toast.error("Failed to load project list");
    } finally {
      setProjectLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      fetchProjects(1, { project_name: projectSearch });
    }, 400);
    return () => clearTimeout(t);
  }, [projectSearch]);

  // ── Debounced search for manual extra items ──
  const handleSearchInputChange = useCallback(
    debounce(async (index, type, row, newValue) => {
      if (type === "category") {
        const productTypeId = row.size?.id;
        if (!productTypeId) {
          setRowCategoryOptions((prev) => ({ ...prev, [index]: [] }));
          return;
        }
        const catData = await getCategories(1, newValue?.trim() || "", productTypeId);
        const list = Array.isArray(catData?.results) ? catData.results : catData;
        setRowCategoryOptions((prev) => ({ ...prev, [index]: list || [] }));
        return;
      }

      if (type === "item" && row.category) {
        const itemData = await fetchItemsByCategoryAndSubcategory(
          row.category.id, 0, 1, newValue?.trim() || ""
        );
        const mappedItems = (itemData?.results || []).map((i) => ({
          id: i.id,
          name: i.name,
          unit_name: i.uom || i.unit_name || "",
          current_stock: parseFloat(i.current_stock || 0),
          size_id: i.size || null,
          size_name: i.size_name || "",
          price: i.price || 0,
        }));
        setItemOptions((prev) => ({ ...prev, [index]: mappedItems }));
        return;
      }

      if (type === "size") {
        const sizeData = await getSizes(newValue?.trim() || "");
        const list = Array.isArray(sizeData?.results) ? sizeData.results : sizeData;
        setSizeOptions(list || []);
      }
    }, 500),
    []
  );

  // ── MOS Selection Change (Instant load without any extra API calls) ──
  const handleMOSChange = (newValue) => {
    if (!newValue) {
      setFormData((prev) => ({ ...prev, project: "" }));
      setRows([createEmptyRow()]);
      setItemOptions({});
      setRowCategoryOptions({});
      return;
    }

    setFormData((prev) => ({ ...prev, project: newValue.id }));

    // Items are already available in selected SalesOrder object!
    const mosItems = (newValue.items || []).map((item) => {
      const sizeObj = item.size_name
        ? sizeOptions.find((s) => s.size === item.size_name || s.name === item.size_name) || { id: item.size || null, size: item.size_name }
        : (item.size ? { id: item.size, size: item.size_name || "" } : null);

      const categoryObj = item.category
        ? { id: item.category, name: item.category_name || "" }
        : null;

      const currentStock = parseFloat(item.current_stock ?? item.stock_qty ?? 0);
      const requiredQty = parseFloat(item.quantity || 0);
      const issuedQty = parseFloat(item.issued_qty || 0);
      const pendingQty = Math.max(0, requiredQty - issuedQty);
      const rate = item.rate !== undefined && item.rate !== null ? String(item.rate) : String(item.price || 0);

      return {
        size: sizeObj,
        category: categoryObj,
        item_description: item.item ? {
          id: item.item,
          name: item.item_name || "",
          unit_name: item.uom || "",
          current_stock: currentStock,
          price: rate,
        } : null,
        uom: item.uom || "",
        description: item.description || item.item_name || "",
        mos_req_qty: requiredQty,
        already_issued: issuedQty,
        pending_qty: pendingQty,
        stock_qty: String(currentStock),
        issue_qty: "0",
        price: rate,
        amount: "0.00",
        is_extra: false,
      };
    });

    setRows(mosItems.length ? mosItems : [createEmptyRow()]);
  };

  // ── Auto-Fill Pending Quantities for All MOS Items ──
  const handleAutoFillPending = () => {
    let filledCount = 0;
    const updatedRows = rows.map((row) => {
      const pending = parseFloat(row.pending_qty || 0);
      const stock = parseFloat(row.stock_qty || 0);
      const rate = parseFloat(row.price || 0);

      if (pending > 0 && stock > 0) {
        const toFill = Math.min(pending, stock);
        filledCount++;
        return {
          ...row,
          issue_qty: String(toFill),
          amount: (toFill * rate).toFixed(2),
        };
      }
      return row;
    });

    setRows(updatedRows);
    if (filledCount > 0) {
      toast.info(`Auto-filled pending quantities for ${filledCount} item(s).`);
    } else {
      toast.warn("No pending requirement or stock available to auto-fill.");
    }
  };

  // ── Clear All Issue Quantities ──
  const handleClearAll = () => {
    const updatedRows = rows.map((r) => ({
      ...r,
      issue_qty: "0",
      amount: "0.00",
    }));
    setRows(updatedRows);
  };

  // ── Row Change Handler ──
  const handleInputChange = async (index, key, value) => {
    const updatedRows = [...rows];
    let row = { ...updatedRows[index] };

    switch (key) {
      case "size":
        row = {
          ...row,
          size: value,
          category: null,
          item_description: null,
          uom: "",
          stock_qty: "0",
          issue_qty: "0",
          price: "0",
          amount: "0.00",
        };
        if (value?.id) {
          try {
            const catData = await getCategories(1, "", value.id);
            const cats = Array.isArray(catData) ? catData : catData?.results || [];
            setRowCategoryOptions((prev) => ({ ...prev, [index]: cats }));
          } catch {
            setRowCategoryOptions((prev) => ({ ...prev, [index]: [] }));
          }
        } else {
          setRowCategoryOptions((prev) => ({ ...prev, [index]: [] }));
        }
        setItemOptions((prev) => ({ ...prev, [index]: [] }));
        break;

      case "category":
        row = {
          ...row,
          category: value,
          item_description: null,
          uom: "",
          stock_qty: "0",
          issue_qty: "0",
          price: "0",
          amount: "0.00",
        };
        if (value?.id) {
          const itemData = await fetchItemsByCategoryAndSubcategory(value.id, 0, 1, "");
          const mappedItems = (itemData.results || []).map((item) => ({
            id: item.id,
            name: item.name,
            unit_name: item.uom || item.unit_name,
            current_stock: parseFloat(item.current_stock || 0),
            size_id: item.size || null,
            size_name: item.size_name || "",
            price: item.price || 0,
          }));
          setItemOptions((prev) => ({ ...prev, [index]: mappedItems }));
        }
        break;

      case "item_description": {
        row.item_description = value;
        if (value?.unit_name) {
          const unit = unitOptions.find(
            (u) => u.name === value.unit_name || u.symbol === value.unit_name
          );
          row.uom = String(unit?.symbol || unit?.name || value.unit_name || "");
        }

        const stock = parseFloat(value?.current_stock || 0);
        row.stock_qty = stock.toString();
        row.price = value?.price !== undefined ? String(value.price) : "0";
        row.description = value?.name || "";
        row.issue_qty = "0";
        row.amount = "0.00";

        if (stock <= 0) {
          toast.warning(`Store stock is 0 for "${value?.name}".`);
        }
        break;
      }

      case "description": {
        row.description = value;
        break;
      }

      case "issue_qty": {
        const issueQty = parseFloat(value) || 0;
        const currentStock = parseFloat(row.stock_qty || 0);

        if (issueQty > currentStock) {
          toast.error(
            `Issue Qty (${issueQty}) exceeds Stock Qty (${currentStock}) in row ${index + 1}.`
          );
          return;
        }

        row.issue_qty = value;
        row.amount = ((parseFloat(row.price) || 0) * issueQty).toFixed(2);
        break;
      }

      case "price": {
        const priceVal = parseFloat(value) || 0;
        row.price = value;
        const qtyVal = parseFloat(row.issue_qty) || 0;
        row.amount = (priceVal * qtyVal).toFixed(2);
        break;
      }

      default:
        row[key] = value;
        break;
    }

    updatedRows[index] = row;
    setRows(updatedRows);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleAddRow = () => setRows([...rows, createEmptyRow(true)]);

  const handleDeleteRow = (index) => {
    const updatedRows = rows.filter((_, i) => i !== index);
    setRows(updatedRows.length ? updatedRows : [createEmptyRow()]);

    const reindex = (prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const key = Number(k);
        if (key < index) next[key] = prev[key];
        else if (key > index) next[key - 1] = prev[key];
      });
      return next;
    };
    setItemOptions(reindex);
    setRowCategoryOptions(reindex);
  };

  const validateForm = () => {
    const errors = {};
    const rowErrs = [];

    if (!formData.slip_no) errors.slip_no = "Slip No. is required";
    if (!formData.labour_name) errors.labour_name = "Department is required";
    if (!formData.project) errors.project = "MOS is required";

    let hasAtLeastOneIssuedItem = false;
    let hasStockError = false;

    rows.forEach((row, i) => {
      const err = {};
      const issueQty = parseFloat(row.issue_qty || 0);
      const stockQty = parseFloat(row.stock_qty || 0);

      if (issueQty > 0) {
        hasAtLeastOneIssuedItem = true;
        const itemId = row.item_description?.id || row.item;
        if (!itemId && !row.item_description) {
          err.item_description = "Required";
        }
        if (issueQty > stockQty) {
          err.issue_qty = `Exceeds stock (${stockQty})`;
          hasStockError = true;
        }
      } else if (issueQty < 0) {
        err.issue_qty = "Cannot be negative";
        hasStockError = true;
      }
      rowErrs[i] = err;
    });

    if (Object.keys(errors).length > 0) {
      if (errors.slip_no) toast.warn(errors.slip_no);
      else if (errors.labour_name) toast.warn(errors.labour_name);
      else if (errors.project) toast.warn(errors.project);
    } else if (hasStockError) {
      toast.error("Issue quantity cannot exceed available stock for one or more items.");
    } else if (!hasAtLeastOneIssuedItem) {
      toast.warn("Please enter Issue Qty (> 0) for at least one item.");
    }

    setFormErrors(errors);
    setRowErrors(rowErrs);

    return (
      Object.keys(errors).length === 0 &&
      rowErrs.every((e) => Object.keys(e).length === 0) &&
      hasAtLeastOneIssuedItem &&
      !hasStockError
    );
  };

  const getTotalAmount = () =>
    rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0).toFixed(2);

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const itemsPayload = rows
        .filter((row) => parseFloat(row.issue_qty || 0) > 0 || isEditMode)
        .map((row) => {
          const existingItem =
            isEditMode &&
            issueData?.items?.find(
              (item) => String(item.item) === String(row.item_description?.id)
            );

          const itemId =
            row.item_description?.id ||
            row.item ||
            (typeof row.item_description === "number" ? row.item_description : existingItem?.item);

          // Find unit ID with case-insensitive matching
          let foundUnit = unitOptions.find(
            (u) =>
              String(u.symbol || "").toLowerCase() === String(row.uom || "").toLowerCase() ||
              String(u.name || "").toLowerCase() === String(row.uom || "").toLowerCase() ||
              String(u.id) === String(row.uom)
          );
          const uomId =
            foundUnit?.id ||
            existingItem?.unit ||
            existingItem?.uom ||
            (row.uom && !isNaN(Number(row.uom)) ? Number(row.uom) : (unitOptions[0]?.id || 1));

          const categoryId =
            row.category?.id ||
            existingItem?.category ||
            (row.category && !isNaN(Number(row.category)) ? Number(row.category) : null);

          const sizeId =
            row.size?.id ||
            existingItem?.size ||
            (row.size && !isNaN(Number(row.size)) ? Number(row.size) : null);

          const desc =
            row.description ||
            row.item_description?.name ||
            (typeof row.item_description === "string" ? row.item_description : existingItem?.item_description) ||
            "";

          return {
            item: itemId,
            category: categoryId,
            item_description: desc,
            uom: uomId,
            stock_qty: parseFloat(row.stock_qty || 0),
            issue_qty: parseFloat(row.issue_qty || 0),
            price: parseFloat(row.price || 0),
            amount: parseFloat(row.amount || 0),
            size: sizeId,
          };
        })
        .filter((item) => item.item);

      // Pre-submit validation: Ensure no item exceeds physical stock
      for (let i = 0; i < itemsPayload.length; i++) {
        const item = itemsPayload[i];
        if (item.issue_qty > item.stock_qty) {
          toast.error(`Row ${i + 1}: Issue Qty (${item.issue_qty}) exceeds available Stock (${item.stock_qty}).`);
          setLoading(false);
          return;
        }
      }

      if (itemsPayload.length === 0) {
        toast.warn("No items selected with Issue Qty > 0.");
        setLoading(false);
        return;
      }

      const divisionData = getSelectedDivision();
      const currentUserId = parseInt(formData.created_by) || parseInt(localStorage.getItem("user_id")) || 1;

      const payload = {
        slip_no: formData.slip_no,
        created_by: currentUserId,
        division: formData.division || divisionData?.division_id || null,
        remarks: formData.remarks || "",
        project: formData.project,
        labour_name: formData.labour_name || "",
        items: itemsPayload,
        total_amount: parseFloat(getTotalAmount()),
      };

      if (isEditMode) {
        payload.go_no = issueData?.go_no;
        await updateIssue(issueData.issue_id, payload);
        toast.success("Issue updated successfully!");
      } else {
        if (!payload.go_no) {
          try {
            const goNoData = await GetNextGoodsOutNo();
            if (goNoData?.go_no) payload.go_no = goNoData.go_no;
          } catch (e) {
            console.error("Failed to fetch fresh go_no:", e);
          }
        }
        await createIssue(payload);
        sessionStorage.removeItem("issueDraft");
        sessionStorage.removeItem("issueRows");
        toast.success("Issue created successfully!");
      }

      setTimeout(() => {
        navigate("/dashboard/issue-list", { state: { refresh: true }, replace: true });
      }, 1000);
    } catch (err) {
      const errData = err?.response?.data;
      console.error("Submit error:", errData);
      if (typeof errData === "string") {
        toast.error(errData);
      } else if (errData?.error) {
        toast.error(errData.error);
      } else if (errData?.go_no) {
        toast.error(`Issue No: ${Array.isArray(errData.go_no) ? errData.go_no[0] : errData.go_no}`);
      } else if (errData?.items) {
        if (Array.isArray(errData.items)) {
          errData.items.forEach((itemErr, index) => {
            if (typeof itemErr === "object" && itemErr !== null) {
              Object.entries(itemErr).forEach(([field, messages]) => {
                const msg = Array.isArray(messages) ? messages[0] : messages;
                toast.error(`Item ${index + 1} (${field}): ${msg}`);
              });
            } else {
              toast.error(String(itemErr));
            }
          });
        } else {
          toast.error(typeof errData.items === "string" ? errData.items : `Items Error: ${JSON.stringify(errData.items)}`);
        }
      } else if (Array.isArray(errData)) {
        errData.forEach((itemErr, index) => {
          if (typeof itemErr === "object" && itemErr !== null) {
            Object.entries(itemErr).forEach(([field, messages]) => {
              const msg = Array.isArray(messages) ? messages[0] : messages;
              toast.error(`Row ${index + 1} (${field}): ${msg}`);
            });
          } else {
            toast.error(String(itemErr));
          }
        });
      } else if (errData?.message) {
        toast.error(errData.message);
      } else if (errData?.detail) {
        toast.error(errData.detail);
      } else if (typeof errData === "object" && errData !== null) {
        const firstKey = Object.keys(errData)[0];
        const firstError = errData[firstKey];
        const msg = Array.isArray(firstError) ? firstError[0] : String(firstError);
        toast.error(`${firstKey}: ${msg}`);
      } else {
        toast.error("Submission failed. Please check form data.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = () => {
    if (isEditMode) return;
    sessionStorage.setItem("issueDraft", JSON.stringify(formData));
    sessionStorage.setItem("issueRows", JSON.stringify(rows));
    setHasDraft(true);
    toast.info("Issue draft saved!");
  };

  const handleDeleteDraft = () => {
    sessionStorage.removeItem("issueDraft");
    sessionStorage.removeItem("issueRows");
    setHasDraft(false);
    setFormData({
      go_no: "",
      slip_no: "",
      labour_name: "",
      division: storedDivision?.division_id || "",
      division_name: storedDivision?.division || "",
      remarks: "",
      project: "",
      created_by: localStorage.getItem("user_id"),
    });
    setRows([createEmptyRow()]);
    fetchNextGoodsOutNo();
    toast.info("Draft deleted");
  };

  return (
    <Box sx={{ p: 2, bgcolor: "background.default", minHeight: "100%" }}>
      {/* Top Action Bar (Original Design) */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mb: 2,
          flexDirection: isMobile ? "column" : "row",
          position: "sticky",
          top: 0,
          zIndex: 1100,
          pt: 1,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          backdropFilter: "blur(8px)",
          boxShadow: isDark ? "0 2px 10px rgba(0,0,0,0.45)" : "0 1px 4px rgba(0,0,0,0.07)",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{
            borderRadius: 0,
            height: 35,
            minWidth: isMobile ? "100%" : 120,
            fontSize: "0.7rem",
            textTransform: "none",
          }}
        >
          ← Back
        </Button>

        {!isEditMode && (
          <Button
            type="button"
            variant="outlined"
            onClick={hasDraft ? handleDeleteDraft : handleSaveDraft}
            sx={{
              borderRadius: 0,
              height: 35,
              minWidth: isMobile ? "100%" : 120,
              fontSize: "0.7rem",
              textTransform: "none",
              color: hasDraft ? "error.main" : "#ff8400",
              borderColor: hasDraft ? "error.main" : "#ff8400",
              "&:hover": {
                borderColor: hasDraft ? "error.dark" : "#e67600",
              },
            }}
          >
            {hasDraft ? "Delete Draft" : "Save Draft"}
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            borderRadius: 0,
            height: 35,
            minWidth: isMobile ? "100%" : 120,
            fontSize: "0.7rem",
            textTransform: "none",
          }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : isEditMode ? "Update" : "Submit"}
        </Button>
      </Box>

      <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />

      {/* Main Form Box (Original Design) */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "8px",
          p: 2,
          bgcolor: "background.paper",
          backgroundImage: "none",
          boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
          mt: 1,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Issue note No."
              value={formData.go_no || "Auto Generated"}
              disabled
              size="small"
              sx={{ "& .MuiInputBase-root": { bgcolor: disabledBg } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Req Slip No"
              required
              fullWidth
              value={formData.slip_no}
              onChange={(e) => handleFormChange("slip_no", e.target.value)}
              error={!!formErrors.slip_no}
              helperText={formErrors.slip_no}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Department"
              required
              fullWidth
              value={formData.labour_name}
              onChange={(e) => handleFormChange("labour_name", e.target.value)}
              error={!!formErrors.labour_name}
              helperText={formErrors.labour_name}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              options={projectOptions}
              getOptionLabel={(opt) => (opt ? (opt.so_no ? `${opt.so_no} — ${opt.project_name || ''}` : (opt.project_name || '')) : "")}
              value={projectOptions.find((opt) => String(opt.id) === String(formData.project)) || null}
              loading={projectLoading}
              onChange={(e, newVal) => handleMOSChange(newVal)}
              onInputChange={(e, newInputValue, reason) => {
                if (reason === "input") setProjectSearch(newInputValue);
              }}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  fullWidth
                  label="MOS"
                  error={!!formErrors.project}
                  helperText={formErrors.project}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    sx: { height: 40, paddingTop: "0px !important", paddingBottom: "0px !important" },
                  }}
                />
              )}
              sx={{
                "& .MuiAutocomplete-inputRoot": { paddingRight: "30px !important" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Remarks"
              fullWidth
              value={formData.remarks}
              onChange={(e) => handleFormChange("remarks", e.target.value)}
              size="small"
            />
          </Grid>
        </Grid>

        {/* Action toolbar for Auto-Fill & Add Loose Item */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3, mb: 1, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
            Material Items ({rows.length})
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FlashOnIcon sx={{ fontSize: 16 }} />}
              onClick={handleAutoFillPending}
              sx={{ fontSize: "0.72rem", textTransform: "none", height: 30 }}
            >
              Auto-Fill Pending
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<ClearAllIcon sx={{ fontSize: 16 }} />}
              onClick={handleClearAll}
              sx={{ fontSize: "0.72rem", textTransform: "none", height: 30 }}
            >
              Clear
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
              onClick={handleAddRow}
              sx={{ fontSize: "0.72rem", textTransform: "none", height: 30 }}
            >
              + Add Loose Item
            </Button>
          </Box>
        </Box>

        {/* Items Section */}
        <Box>
          {isMobile ? (
            // ── MOBILE CARDS ──
            <Box>
              {rows.map((row, index) => (
                <Card
                  key={index}
                  elevation={0}
                  sx={{
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    boxShadow: isDark ? "0 2px 10px rgba(0,0,0,0.35)" : "none",
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="h6" sx={{ color: "primary.main", fontSize: "0.9rem", fontWeight: 700 }}>
                        Item #{index + 1}
                      </Typography>
                      <IconButton onClick={() => handleDeleteRow(index)} color="error" size="small">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Typography fontWeight={600} sx={{ fontSize: "0.85rem" }}>
                        {row.item_description?.name || "Select Item"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.category?.name || "—"} | UOM: {row.uom || "—"}
                      </Typography>

                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">MOS Req.</Typography>
                          <Typography fontWeight={600} sx={{ fontSize: "0.8rem" }}>{row.mos_req_qty || "—"}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Pending</Typography>
                          <Typography fontWeight={700} color="warning.main" sx={{ fontSize: "0.8rem" }}>
                            {row.pending_qty ?? "—"}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Stock Qty</Typography>
                          <Typography fontWeight={700} sx={{ fontSize: "0.8rem" }}>
                            {row.stock_qty}
                          </Typography>
                        </Grid>
                      </Grid>

                      <TextField
                        label="Description"
                        fullWidth
                        size="small"
                        value={row.description || ""}
                        onChange={(e) => handleInputChange(index, "description", e.target.value)}
                        placeholder="Description..."
                      />

                      <TextField
                        label="Issue Qty"
                        type="number"
                        required
                        fullWidth
                        size="small"
                        value={row.issue_qty}
                        onChange={(e) => handleInputChange(index, "issue_qty", e.target.value)}
                        error={!!rowErrors[index]?.issue_qty}
                        helperText={rowErrors[index]?.issue_qty}
                        InputProps={{ inputProps: { min: 0 } }}
                      />

                      <TextField
                        label="Rate"
                        type="number"
                        required
                        fullWidth
                        size="small"
                        value={row.price}
                        onChange={(e) => handleInputChange(index, "price", e.target.value)}
                        error={!!rowErrors[index]?.price}
                        helperText={rowErrors[index]?.price}
                        InputProps={{ inputProps: { min: 0, step: "any" } }}
                      />

                      <TextField
                        label="Amount"
                        type="number"
                        fullWidth
                        size="small"
                        value={row.amount}
                        disabled
                        sx={{ "& .MuiInputBase-root": { bgcolor: disabledBg } }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}

              {rows.length > 0 && (
                <Card
                  elevation={0}
                  sx={{
                    mt: 2,
                    bgcolor: isDark ? alpha(theme.palette.primary.main, 0.12) : "#e3f2fd",
                    border: "1px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" align="right" sx={{ color: "primary.main", fontWeight: 700 }}>
                      Total Amount: ₹ {parseFloat(getTotalAmount()).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : (
            // ── DESKTOP TABLE (Original Table Theme & Colors) ──
            <TableContainer
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
                boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
                overflowX: "auto",
              }}
            >
              <Table
                size="small"
                sx={{
                  minWidth: 1050,
                  "& td, & th": {
                    textAlign: "center",
                    padding: "8px",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  },
                }}
              >
                <TableHead
                  sx={{
                    "& th": {
                      fontWeight: "bold",
                      color: "text.primary",
                      borderBottom: "2px solid",
                      borderColor: "primary.main",
                      backgroundColor: `${tableHeadBg} !important`,
                      fontSize: "0.75rem",
                      letterSpacing: "0.04em",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell>Sr. No.</TableCell>
                    <TableCell align="left">Item Name</TableCell>
                    <TableCell align="left">Description</TableCell>
                    <TableCell>UOM</TableCell>
                    <TableCell>MOS Req.</TableCell>
                    <TableCell>Issued</TableCell>
                    <TableCell>Pending</TableCell>
                    <TableCell>Stock Qty.</TableCell>
                    <TableCell>Issue Qty</TableCell>
                    <TableCell>Rate</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Delete</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        bgcolor: rowBg(index),
                        "&:hover": {
                          bgcolor: isDark ? alpha(theme.palette.primary.main, 0.08) : "#f0f7ff",
                        },
                        transition: "background-color 0.15s ease",
                      }}
                    >
                      <TableCell sx={{ color: "text.secondary" }}>{index + 1}</TableCell>

                      {/* Item Description */}
                      <TableCell align="left" sx={{ minWidth: 160 }}>
                        {row.is_extra ? (
                          <Autocomplete
                            options={mergeSelectedIntoOptions(itemOptions[index] || [], row.item_description)}
                            getOptionLabel={(option) => option.name || ""}
                            isOptionEqualToValue={(o, v) => o?.id === v?.id}
                            value={row.item_description || null}
                            onChange={(e, newVal, reason) => {
                              handleInputChange(index, "item_description", reason === "clear" ? null : newVal);
                            }}
                            onInputChange={(e, newValue, reason) => {
                              if (reason === "input") handleSearchInputChange(index, "item", row, newValue);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required
                                label="Item"
                                size="small"
                                error={!!rowErrors[index]?.item_description}
                                helperText={rowErrors[index]?.item_description}
                              />
                            )}
                          />
                        ) : (
                          <Box>
                            <Typography fontWeight={600} sx={{ fontSize: "0.78rem", lineHeight: 1.3 }}>
                              {row.item_description?.name || "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                              {row.category?.name || "—"}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>

                      {/* Description Field */}
                      <TableCell align="left" sx={{ minWidth: 180 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.description || ""}
                          onChange={(e) => handleInputChange(index, "description", e.target.value)}
                          placeholder="Description..."
                          sx={{
                            "& input": { fontSize: "0.75rem", py: "4px" },
                          }}
                        />
                      </TableCell>

                      {/* UOM */}
                      <TableCell sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                        {row.uom || "—"}
                      </TableCell>

                      {/* MOS Req Qty */}
                      <TableCell sx={{ fontSize: "0.78rem", fontWeight: 600 }}>
                        {row.is_extra ? "—" : row.mos_req_qty}
                      </TableCell>

                      {/* Already Issued */}
                      <TableCell sx={{ fontSize: "0.78rem" }}>
                        {row.is_extra ? "—" : row.already_issued}
                      </TableCell>

                      {/* Pending Qty */}
                      <TableCell sx={{ fontSize: "0.78rem", fontWeight: 700, color: (row.pending_qty > 0 ? "warning.main" : "text.secondary") }}>
                        {row.is_extra ? "—" : row.pending_qty}
                      </TableCell>

                      {/* Stock Qty */}
                      <TableCell>
                        <TextField
                          type="number"
                          value={row.stock_qty}
                          disabled
                          size="small"
                          sx={{ width: "90px", "& .MuiInputBase-root": { bgcolor: disabledBg } }}
                        />
                      </TableCell>

                      {/* Issue Qty */}
                      <TableCell>
                        <TextField
                          type="number"
                          required
                          value={row.issue_qty}
                          onChange={(e) => handleInputChange(index, "issue_qty", e.target.value)}
                          error={!!rowErrors[index]?.issue_qty}
                          helperText={rowErrors[index]?.issue_qty}
                          size="small"
                          sx={{ width: "90px" }}
                          InputProps={{ inputProps: { min: 0 } }}
                        />
                      </TableCell>

                      {/* Rate */}
                      <TableCell>
                        <TextField
                          type="number"
                          required
                          value={row.price}
                          onChange={(e) => handleInputChange(index, "price", e.target.value)}
                          error={!!rowErrors[index]?.price}
                          helperText={rowErrors[index]?.price}
                          size="small"
                          sx={{ width: "85px" }}
                          InputProps={{ inputProps: { min: 0, step: "any" } }}
                        />
                      </TableCell>

                      {/* Amount */}
                      <TableCell>
                        <TextField
                          type="number"
                          value={row.amount}
                          disabled
                          size="small"
                          sx={{ width: "95px", "& .MuiInputBase-root": { bgcolor: disabledBg } }}
                        />
                      </TableCell>

                      {/* Delete */}
                      <TableCell>
                        <Tooltip title="Delete Row">
                          <IconButton onClick={() => handleDeleteRow(index)} color="error" size="small">
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <TableFooter>
                  <TableRow
                    sx={{
                      bgcolor: isDark ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.primary.main, 0.05),
                      "& td": {
                        borderTop: "2px solid",
                        borderColor: "primary.main",
                        color: "text.primary",
                        fontWeight: "bold",
                        borderBottom: "none",
                      },
                    }}
                  >
                    <TableCell colSpan={10} align="right" sx={{ fontWeight: "bold" }}>
                      Total Amount:
                    </TableCell>
                    <TableCell colSpan={2} sx={{ fontWeight: "bold", color: "primary.main" }}>
                      ₹ {parseFloat(getTotalAmount()).toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
}